/**
 * Client-shell adapter for the universal object + task peeks (RO-10-09
 * precondition / #44566).
 *
 * Next mounts `ObjectPeekHost` + `TaskPeekHost` in `app/layout.tsx`. The Vite
 * shell had the overlay *slot* (`chrome.overlays`) but nothing filled it, so
 * `?peek=<uid>` navigated without a `[role=dialog]` and the
 * `workflow-dod-surfaces` suite failed at step 2 on both the dialog assert and
 * every DoD value behind it.
 *
 * This adapter is the READ half required by that suite:
 *   · `ObjectPeekHost` opens the dialog from `?peek=`
 *   · `TaskPeekHost` rewrites legacy `?task=` into `?peek=`
 *   · `TaskDodSection` is the body extension that paints `gate:pnpm…`,
 *     "gates done", "Work class", and the Correct… checklist
 *
 * Write affordances (ObjectEditController / archive / auto-verify server
 * actions) stay on the Next host for now — the suite asserts `Save correction`
 * is present and never clicks it, and never archives. Expanding writes is a
 * later cutover item, not this precondition.
 */

import {
  ObjectPeekHost as BaseObjectPeekHost,
  TaskPeekHost,
  TaskDodSection,
  SessionClaimsPanel,
  TaskEvidenceArtifactSection,
} from "@excellent/ui";
import { usePathname } from "@excellent/ui/router";
import type { ObjectPeekDto } from "@excellent/ui/api/contracts/objects";
import { isModeSlug } from "../../src/modes/modes-registry";

export function ClientObjectPeekHost({ dialogEnabled = true }: { dialogEnabled?: boolean } = {}) {
  const pathname = usePathname();
  const first = pathname.split("/").filter(Boolean)[0];
  const from = first && isModeSlug(first) ? first : null;

  return (
    <>
      <TaskPeekHost />
      {dialogEnabled ? (
        <BaseObjectPeekHost
          from={from}
          bodyExtension={(obj: ObjectPeekDto) =>
            obj.kind === "task" && obj.localId != null ? (
              <>
                {/* P2-U3: verified-done evidence artifact (renders only when
                    the P2-02 production emitter wrote one). */}
                <TaskEvidenceArtifactSection taskId={obj.localId} />
                <TaskDodSection taskId={obj.localId} />
                {/* P2-U2: what the P2-01 claim gate evaluates, beside the DoD. */}
                <SessionClaimsPanel taskId={obj.localId} />
              </>
            ) : null
          }
        />
      ) : null}
    </>
  );
}
